import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useCurrentUser from "hooks/useCurrentUser";
import { isAnyPermitted } from "util/PermissionsMixin";
import useCollectionMutation from "collections/hooks/useCollectionMutation";
import { ButtonToolbar, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { ConfirmDialog, IfPermitted, ShareButton } from "components/common";
import { MoreActions } from "components/common/EntityDataTable";
import EntityShareModal from "components/permissions/EntityShareModal";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import CollectionsRoutes from "../../Routes";
const CollectionActions = ({ id, title }) => {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const refetchCollections = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY]
    }),
    [queryClient]
  );
  const { deselectEntity } = useSelectedEntities();
  const { deleteCollection } = useCollectionMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCollectionShareModal, setShowCollectionShareModal] = useState(false);
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState(false);
  const toggleCollectionShareModal = () => setShowCollectionShareModal(!showCollectionShareModal);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);
  const handleShareClose = (type) => {
    refetchCollections();
    if (type === "collection") {
      toggleCollectionShareModal();
    } else if (type === "collectionContent") {
      toggleCollectionContentShareModal();
    }
  };
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    deselectEntity(id);
  };
  const handleDelete = () => {
    deleteCollection({ id }).then(() => {
      deselectEntity(id);
      setShowDeleteDialog(false);
    });
  };
  const onDelete = () => {
    setShowDeleteDialog(true);
  };
  const enableDropdownMenu = isAnyPermitted(currentUser.permissions, [
    `collection_entities:edit:${id}`,
    `collections:edit:${id}`,
    `collections:delete:${id}`
  ]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showDeleteDialog && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showDeleteDialog,
      title: "Deleting Collection",
      onCancel: cancelDelete,
      onConfirm: handleDelete
    },
    /* @__PURE__ */ React.createElement("p", null, 'You are about to delete the Collection: "', title, '". Are you sure?')
  ), showCollectionContentShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: id,
      entityType: "collection_entities",
      entityTypeTitle: "Collection Content",
      entityTitle: title,
      description: "Search for a User or Team to add as collaborator on this Collection Content.",
      onClose: () => handleShareClose("collectionContent")
    }
  ), showCollectionShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: id,
      entityType: "collection",
      entityTypeTitle: "Collection",
      entityTitle: title,
      description: "Search for a User or Team to add as collaborator on this Collection.",
      onClose: () => handleShareClose("collection")
    }
  ), /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      title: "Share Content",
      entityId: id,
      entityType: "collection_entities",
      onClick: toggleCollectionContentShareModal,
      bsSize: "xs",
      bsStyle: "primary",
      showTitle: true
    }
  ), /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      entityId: id,
      entityType: "collection",
      onClick: toggleCollectionShareModal,
      bsSize: "xs",
      title: "Manage",
      showTitle: true
    }
  ), /* @__PURE__ */ React.createElement(MoreActions, { disabled: !enableDropdownMenu }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`collection_entities:edit:${id}`, `collection:edit:${id}`], anyPermissions: true }, /* @__PURE__ */ React.createElement(MenuItem, { href: CollectionsRoutes.show(id) }, "Edit")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collections:delete:${id}` }, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDelete }, "Delete")))));
};
export default CollectionActions;
